import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Youtube, Twitter, Mail, Phone, MapPin } from "lucide-react";
// Pinterest doesn't ship in lucide-react by default — use a tiny inline SVG.
const PinterestIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M8 12a4 4 0 1 1 8 0c0 3-3 5-3 5" />
    <path d="M12 3v0a9 9 0 0 0-4.5 16.5" />
    <path d="M9 21c-.7-2 .5-4.5 1-6.5" />
  </svg>
);
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { usePublicSettings } from "@/lib/publicSettings";

// Iter54a — CMS-driven social icon map. Only icons present here render, and
// only if the admin has entered a URL in Social Settings. Extend the map to
// support new networks without touching JSX below.
const SOCIAL_ICONS = {
  facebook: Facebook,
  instagram: Instagram,
  linkedin: Linkedin,
  youtube: Youtube,
  twitter: Twitter,
  pinterest: PinterestIcon,
};

const Footer = () => {
  const [email, setEmail] = useState("");
  const { settings } = usePublicSettings();

  const brandName = settings?.company?.name || "Dream Home Engineers";
  const brandTagline = settings?.company?.tagline || "Design · Plan · Build · Deliver";
  // Iter48 Phase C / Iter54a — footer logo respects the dedicated
  // footer_logo_url first (so admins can ship a colour-variant for the dark
  // footer), then main logo, then bundled fallback. Width is CMS-driven.
  const cmpny = settings?.company || {};
  const logoUrl = cmpny.footer_logo_url || cmpny.logo_url || "/dhe-logo.png";
  const footerLogoWidth = cmpny.footer_logo_width || "";
  const logoDisplayMode = cmpny.logo_display_mode || "contain";
  const footerLogoStyle = {
    maxWidth: footerLogoWidth || undefined,
    width: footerLogoWidth ? "100%" : "auto",
    objectFit: logoDisplayMode,
  };
  const about = settings?.company?.about_short || "Architectural design, BIM, CAD, 3D modeling & rendering. Curated house plans, delivered to homeowners, builders, and architects worldwide.";
  const contact = settings?.contact || {};
  const social = settings?.social || {};
  const primaryEmail = (contact.emails || [])[0];
  const primaryPhone = (contact.phones || [])[0];
  const addressParts = [contact.address_line1, contact.address_line2, contact.city, contact.state, contact.country].filter(Boolean).join(", ");

  const submitNewsletter = (e) => {
    e.preventDefault();
    if (!email) return;
    toast.success("Subscribed", { description: "We'll email you when new plans drop." });
    setEmail("");
  };

  return (
    <footer data-testid="site-footer" className="mt-24 bg-primary text-primary-foreground">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        {/* Newsletter */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 pb-16 border-b border-primary-foreground/10">
          <div className="lg:col-span-6">
            <div className="overline text-accent">Newsletter</div>
            <h3 className="font-display text-4xl lg:text-5xl mt-3 leading-tight">New plans. Every fortnight.</h3>
            <p className="mt-4 text-primary-foreground/70 max-w-md">
              Join 12,000+ homeowners and builders getting first access to our newest architectural plans and design guides.
            </p>
          </div>
          <div className="lg:col-span-6 lg:pl-12 flex items-center">
            <form onSubmit={submitNewsletter} className="flex gap-3 w-full max-w-lg" data-testid="newsletter-form">
              <Input
                type="email"
                placeholder="you@address.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="newsletter-email"
                className="rounded-none h-14 bg-transparent border-primary-foreground/30 text-primary-foreground placeholder:text-primary-foreground/50 focus-visible:ring-accent"
              />
              <Button type="submit" data-testid="newsletter-submit" className="rounded-none h-14 px-8 bg-accent hover:bg-accent/90 text-accent-foreground">
                Subscribe
              </Button>
            </form>
          </div>
        </div>

        {/* Columns */}
        <div className="grid grid-cols-2 lg:grid-cols-12 gap-10 py-16">
          <div className="col-span-2 lg:col-span-4">
            <div className="flex items-center gap-3">
              {logoUrl ? (
                // Iter54a — wrapper removed; admin controls width via CMS.
                <img
                  src={logoUrl}
                  alt={brandName}
                  className="h-auto max-h-20"
                  style={footerLogoStyle}
                  data-testid="footer-brand-name"
                />
              ) : (
                <>
                  <div className="w-10 h-10 flex items-center justify-center bg-accent text-primary font-display text-2xl leading-none pt-1">{brandName.charAt(0)}</div>
                  <div className="leading-tight">
                    <div className="font-display text-2xl" data-testid="footer-brand-name">{brandName}</div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-accent">{brandTagline}</div>
                  </div>
                </>
              )}
            </div>
            <p className="mt-6 text-primary-foreground/70 max-w-xs text-sm leading-relaxed" data-testid="footer-about">
              {about}
            </p>
            <div className="mt-8 flex items-center gap-3" data-testid="footer-social">
              {Object.entries(SOCIAL_ICONS).map(([k, Icon]) => {
                const url = social[k];
                if (!url) return null;
                return (
                  <a key={k} href={url} aria-label={k} target="_blank" rel="noreferrer" data-testid={`footer-social-${k}`} className="w-9 h-9 flex items-center justify-center border border-primary-foreground/20 hover:border-accent hover:text-accent transition-fast">
                    <Icon size={16} />
                  </a>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="overline text-accent">Company</div>
            <ul className="mt-4 space-y-2.5 text-sm text-primary-foreground/80">
              <li><Link to="/about" className="hover:text-accent transition-fast">About Us</Link></li>
              <li><Link to="/services" className="hover:text-accent transition-fast">Services</Link></li>
              <li><Link to="/custom-design" className="hover:text-accent transition-fast">Custom Design</Link></li>
              <li><Link to="/contact" className="hover:text-accent transition-fast">Contact</Link></li>
              <li><Link to="/blog" className="hover:text-accent transition-fast">Blog</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="overline text-accent">House Plans</div>
            <ul className="mt-4 space-y-2.5 text-sm text-primary-foreground/80">
              <li><Link to="/plans?style=Modern" className="hover:text-accent transition-fast">Modern</Link></li>
              <li><Link to="/plans?style=Luxury+Villa" className="hover:text-accent transition-fast">Luxury Villas</Link></li>
              <li><Link to="/plans?style=Farmhouse" className="hover:text-accent transition-fast">Farmhouse</Link></li>
              <li><Link to="/plans?style=Duplex" className="hover:text-accent transition-fast">Duplex</Link></li>
              <li><Link to="/plans?style=Traditional" className="hover:text-accent transition-fast">Traditional</Link></li>
              <li><Link to="/plans?style=Small" className="hover:text-accent transition-fast">Small Homes</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="overline text-accent">Services</div>
            <ul className="mt-4 space-y-2.5 text-sm text-primary-foreground/80">
              <li><Link to="/services#architectural" className="hover:text-accent transition-fast">Architectural Design</Link></li>
              <li><Link to="/services#bim" className="hover:text-accent transition-fast">BIM & Revit</Link></li>
              <li><Link to="/services#cad" className="hover:text-accent transition-fast">CAD Drafting</Link></li>
              <li><Link to="/services#3d" className="hover:text-accent transition-fast">3D Rendering</Link></li>
              <li><Link to="/services#modeling" className="hover:text-accent transition-fast">3D Modeling</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="overline text-accent">Support</div>
            <ul className="mt-4 space-y-2.5 text-sm text-primary-foreground/80">
              <li><Link to="/contact" className="hover:text-accent transition-fast">Help Center</Link></li>
              <li><Link to="/purchases" className="hover:text-accent transition-fast">My Plans</Link></li>
              <li><Link to="/privacy" className="hover:text-accent transition-fast">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-accent transition-fast">Terms of Service</Link></li>
              <li><Link to="/refund" className="hover:text-accent transition-fast">Refund Policy</Link></li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-primary-foreground/10 flex flex-col md:flex-row justify-between text-xs text-primary-foreground/50 gap-3">
          <span data-testid="footer-copyright">© {new Date().getFullYear()} {brandName}. All rights reserved{addressParts ? ` · ${addressParts}` : ""}.</span>
          <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-4">
            {primaryEmail && (
              <a href={`mailto:${primaryEmail}`} data-testid="footer-email" className="hover:text-accent flex items-center gap-1.5">
                <Mail size={12} /> {primaryEmail}
              </a>
            )}
            {primaryPhone && (
              <a href={`tel:${primaryPhone.replace(/\s+/g, "")}`} data-testid="footer-phone" className="hover:text-accent flex items-center gap-1.5">
                <Phone size={12} /> {primaryPhone}
              </a>
            )}
            {addressParts && (
              <span data-testid="footer-address" className="flex items-center gap-1.5">
                <MapPin size={12} /> {contact.city}, {contact.country}
              </span>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
